// Designer-side TxnScreenRuntime: fills the shared end-user transaction
// screen's I/O contract (@twasta/page-runtime/txn/screen) with the platform's
// /api/livepreview/* endpoints, already bound to one project. This preserves
// exactly how the designer Live Preview tab and the Run view behave today —
// every method is a one-line binding onto the existing api.ts functions.

import { saveTransactionObject } from '@/api/designers'
import type { TxnScreenRuntime } from '@twasta/page-runtime/txn/screen'
import type { TxnRuntime } from '@twasta/page-runtime/txn'
import type { LoadResponse, ListResponse } from '@twasta/page-runtime/txn/contracts'
import type { WizardVia } from '@twasta/page-runtime/wizard/contract'
import { makeApprovalsRuntime } from '@twasta/page-runtime/txn/screen'
import type { ApprovalsInboxApprovals } from '@twasta/page-runtime/txn/approvals'
import {
  livePreviewAuditLog, livePreviewDelete, livePreviewExecute,
  livePreviewExecuteBatch, livePreviewList, livePreviewLoad,
  livePreviewRecordHistory, livePreviewResolveDefaults, livePreviewRights,
  modelOverlayCanCustomize, modelOverlayPatch,
} from '../api'
import { platformCustomizeTransport } from './customizeTransport'
import { platformBuildTransport } from './buildTransport'
import type { TxnBuildRuntime } from '@twasta/page-runtime/customize'
import { canEditObjectsFor } from '@/auth/useProjectPermissions'
import { platformUdaTransport } from './udaTransport'
import { makeDesignerTxnRuntime } from './designerTxnRuntime'
import { loadModel } from './loadModel'
import { getRecordAliases, setRecordAliases } from '@/api/vectorSearch'

export function makeDesignerScreenRuntime(
  project: string,
  opts: {
    /** Deploy-on-mount gate (the designer owns the deploy UI/state; the
     *  screen awaits this before its first fetch and re-runs it when the
     *  host bumps versionKey). Should throw on failure AFTER surfacing it. */
    ensureReady?: (objName: string) => Promise<void>
    /** handler='open' action buttons: open another object (the host decides
     *  between navigating and its drawer). Absent → the button says it is not
     *  available here. */
    openObject?: TxnRuntime['openObjectAction']
    /** handler='wizard' action buttons (navigate or drawer — the host
     *  decides). Absent → the wizard opens in a slide-over. */
    openWizard?: TxnRuntime['openWizardAction']
    /** The screen runs INSIDE a Transaction Wizard: every request body
     *  (execute / execute-batch / lookup / resolve-defaults, and list / load,
     *  which then ride the execute funnel) carries `wizard: via`, so the
     *  server authorizes it against the wizard's grant (design §12.2). */
    wizard?: WizardVia
    /** Make changes (the screen itself, for people who build the app): the
     *  host's own hooks — Save a version, the full designer, Ask AI, and
     *  whether the AI is building the screen right now. Absent ⇒ not
     *  offered (the Run view, drawers). */
    build?: Pick<TxnBuildRuntime, 'readOnlyReason' | 'saveVersion' | 'openDesigner' | 'askAi' | 'hostButton'>
  } = {},
  // The designer binds the FULL approval family (via makeApprovalsRuntime), so
  // `approvals` is the tight inbox shape. getModel stays the shim's job (the
  // designer owns the model loader), so this is NOT a full ApprovalsInboxRuntime
  // — the inbox shim adds getModel to reach that contract cast-free.
): TxnScreenRuntime & { approvals: ApprovalsInboxApprovals } {
  // The generic execute funnel, shared by the `execute` field and the
  // approval action family (makeApprovalsRuntime) so both route identically.
  const via = opts.wizard
  const viaOpts = via ? { wizard: via } : undefined
  const execute: TxnScreenRuntime['execute'] = (objName, action, payload) =>
    livePreviewExecute(project, objName, action, payload, viaOpts)
  return {
    txn: {
      ...makeDesignerTxnRuntime(project, undefined, undefined, {
        wizard: via, openWizard: opts.openWizard,
      }),
      ...(opts.openObject ? { openObjectAction: opts.openObject } : {}),
    },
    // Under a wizard, list / load go through the execute funnel (the same
    // engine actions the GET endpoints run) so they carry the wizard too — a
    // GET has no body, and a wizard-only user holds no grant on the base T.
    list: (objName, q) => (via
      ? execute(objName, 'list', {
        limit: q.limit, offset: q.offset,
        ...(q.explicitLimit ? { explicit_limit: true } : {}),
        ...(q.formNo ? { form_no: q.formNo } : {}),
        ...(q.parentPk && Object.keys(q.parentPk).length ? { parent_pk: q.parentPk } : {}),
        ...(q.filters?.length ? { filters: q.filters } : {}),
        ...(q.enforcedOverrides && Object.keys(q.enforcedOverrides).length
          ? { enforced_overrides: q.enforcedOverrides } : {}),
        ...(q.bypass ? { bypass_enforced: true }
          : q.filterSetId ? { enforced_filter_set_id: q.filterSetId } : {}),
      }) as Promise<ListResponse>
      : livePreviewList(
        project, objName, q.limit, q.offset, q.formNo, q.parentPk, q.filters,
        q.enforcedOverrides, { filterSetId: q.filterSetId, bypass: q.bypass },
        q.explicitLimit,
      )),
    load: (objName, txnId) => (via
      ? execute(objName, 'load', { txn_id: txnId }) as Promise<LoadResponse>
      : livePreviewLoad(project, objName, txnId)),
    saveBatch: (objName, body) => livePreviewExecuteBatch(project, objName, body, viaOpts),
    remove: (objName, txnId) => livePreviewDelete(project, objName, txnId),
    execute,
    resolveDefaults: (objName) => livePreviewResolveDefaults(project, objName, viaOpts),
    recordHistory: livePreviewRecordHistory,
    // "Also known as" — only reached when the object's model turns aliases on
    // (see TxnScreen), so binding them unconditionally costs nothing here.
    recordAliases: getRecordAliases,
    saveRecordAliases: setRecordAliases,
    auditLog: livePreviewAuditLog,
    // The viewer's rights on the object, so the Run view hides the Add /
    // Edit / Delete and custom buttons a role is not granted instead of
    // offering them and refusing on click. An author of the app is answered
    // with full rights (see the endpoint), so the designer preview is
    // unchanged.
    rights: (objName) => livePreviewRights(project, objName),
    // Any object's model, for surfaces that render a record from ANOTHER
    // object: the approvals review panel, and the process trail's right-hand
    // pane (which draws the linked record with the real <FormRenderer>).
    getModel: (objName) => loadModel(project, objName),
    approvals: makeApprovalsRuntime(execute),
    // Customize mode (⋮ → Customize): the overlay endpoints for this
    // project; the server decides who may and what may change.
    customize: {
      transport: platformCustomizeTransport(project),
      canCustomize: () => modelOverlayCanCustomize(project, 'T'),
    },
    ...(opts.build ? {
      build: {
        transport: platformBuildTransport(project),
        canBuild: () => canEditObjectsFor(project),
        ...opts.build,
      },
    } : {}),
    // Additional attributes: which apply is asked through `execute`
    // (uda_resolve) only for forms the engine marks as having them; this is
    // the setup transport (/api/uda/{project}/*) for Customize.
    uda: platformUdaTransport(project),
    layout: {
      // 'design': bake width/order into the shared transaction JSON.
      saveDesign: (proj, objName, model) =>
        saveTransactionObject(proj, objName, { transaction: model }),
      // 'customize': store the leaves as a per-tenant model overlay.
      saveOverlay: (proj, objName, set) =>
        modelOverlayPatch(proj, objName, set, [], 'T'),
    },
    ensureReady: opts.ensureReady,
  }
}
